/**
 * Shared by the Insurer and Sub-Agent commission rate screens: the optional Product Category /
 * State / RTO scope of a rate (design doc 2026-11-03-rate-scope-matching-design.md).
 *
 * A screen keeps one `RateScope`. Saving turns it into one rate per ticked state (each with that
 * state's own RTO text), sent as a single rate, or, for two or more states, as one all-or-nothing
 * batch. The server cleans and checks everything again; this only shapes the request and the message.
 */
import { extractErrorMessage } from '@/lib/motor-insurance/error-utils';

export const PAN_INDIA = 'Pan India';

export interface CategoryOption {
  code: string;
  label: string;
}

export interface StateOption {
  code: string;
  name: string;
}

export interface ScopeOptions {
  categories: CategoryOption[];
  states: StateOption[];
}

export const NO_SCOPE_OPTIONS: ScopeOptions = { categories: [], states: [] };

/** Reads GET .../scope-options; any other shape gives empty options instead of breaking the screen. */
export function parseScopeOptions(data: unknown): ScopeOptions {
  const { product_categories, states } = (data ?? {}) as { product_categories?: unknown; states?: unknown };
  return {
    categories: Array.isArray(product_categories) ? (product_categories as CategoryOption[]) : [],
    states: Array.isArray(states) ? (states as StateOption[]) : [],
  };
}

export interface RateScope {
  /** The category as typed; a label only. */
  category: string;
  /** Ticked states; none means Pan India. */
  stateCodes: string[];
  /** RTO text per ticked state. */
  rtoByState: Record<string, string>;
}

export const EMPTY_SCOPE: RateScope = { category: '', stateCodes: [], rtoByState: {} };

/** RTO numbers belong to one state, so the text of a state that is no longer ticked is dropped. */
export function withStates(scope: RateScope, next: string[]): RateScope {
  return {
    ...scope,
    stateCodes: next,
    rtoByState: Object.fromEntries(Object.entries(scope.rtoByState).filter(([code]) => next.includes(code))),
  };
}

export function withRto(scope: RateScope, stateCode: string, text: string): RateScope {
  return { ...scope, rtoByState: { ...scope.rtoByState, [stateCode]: text } };
}

export function categoryLabel(code: string | null, categories: CategoryOption[]): string {
  if (!code) return '—';
  return categories.find((c) => c.code === code)?.label || code;
}

export function stateLabel(code: string | null, states: StateOption[]): string {
  if (!code) return PAN_INDIA;
  return states.find((s) => s.code === code)?.name || code;
}

/** One rate per ticked state, or the base rate alone. Empty scope fields are left out entirely. */
export function buildScopedPayloads<B extends object>(base: B, scope: RateScope): Array<B & Record<string, unknown>> {
  const category = scope.category.trim();
  const withCategory = { ...base, ...(category ? { product_category: category } : {}) };
  if (scope.stateCodes.length === 0) return [withCategory];
  return scope.stateCodes.map((code) => {
    const rtoCodes = (scope.rtoByState[code] ?? '').trim();
    return { ...withCategory, state_code: code, ...(rtoCodes ? { rto_codes: rtoCodes } : {}) };
  });
}

/**
 * The server words a bad entry as "Value error, <message>"; show the message. When the refusal belongs to
 * one entry of a several-states save ("rates.1.rto_codes"), say which state that entry was.
 */
export function describeSaveError(
  body: unknown,
  fallback: string,
  orderedStateCodes: string[],
  states: StateOption[]
): string {
  const message = extractErrorMessage(body, fallback).replace(/^Value error,\s*/i, '');
  const field = (body as { error?: { field?: string } } | null)?.error?.field ?? '';
  const entry = /^rates\.(\d+)(?:\.|$)/.exec(field);
  const code = entry ? orderedStateCodes[Number(entry[1])] : undefined;
  return code ? `${stateLabel(code, states)}: ${message}` : message;
}

export interface SaveScopedRatesArgs {
  post: (url: string, body: unknown) => Promise<Response>;
  url: string;
  batchUrl: string;
  base: object;
  scope: RateScope;
  states: StateOption[];
}

/** Saves the rate for the chosen scope and returns the saved rows. Throws an Error with a readable message. */
export async function saveScopedRates({ post, url, batchUrl, base, scope, states }: SaveScopedRatesArgs): Promise<unknown[]> {
  const payloads = buildScopedPayloads(base, scope);
  const batch = payloads.length >= 2;
  const res = await post(batch ? batchUrl : url, batch ? { rates: payloads } : payloads[0]);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(describeSaveError(body, `Save failed (${res.status})`, scope.stateCodes, states));
  }
  const saved = await res.json().catch(() => null);
  return Array.isArray(saved) ? saved : saved ? [saved] : [];
}
